import { useState } from "react"
import {
  X,
  Shield,
  ExternalLink,
  Download,
  ArrowLeft,
  Info,
} from "lucide-react"
import appleIcon from "../assets/icons/apple.png"
import windowsIcon from "../assets/icons/windows.png"

const MAC_URL =
  "https://storage.googleapis.com/interviewguard-downloads/InterviewGuard.dmg"
const WIN_URL =
  "https://apps.microsoft.com/detail/9p6j1z502m3t?hl=en-GB&gl=IN"

const IMG_MAC =
  "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1774182085/mac_pare8j.png"
const IMG_WIN1 =
  "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1774182085/windows1_ks0yki.png"
const IMG_WIN2 =
  "https://res.cloudinary.com/do0q4oj8x/image/upload/f_auto,q_auto/v1774182086/windows2_xij4xm.png"

const StepKey = ({ children }) => (
  <span
    style={{
      display: "inline",
      background: "rgba(0,0,0,0.06)",
      border: "0.5px solid rgba(0,0,0,0.12)",
      borderRadius: 4,
      padding: "1px 6px",
      fontSize: 11.5,
      fontWeight: 500,
      fontFamily: "monospace",
      color: "inherit",
    }}
  >
    {children}
  </span>
)

const macSteps = [
  {
    label: "A prompt will appear asking to confirm",
    desc: (
      <>
        After opening the .dmg, macOS shows this dialog. Apple has already
        scanned the file and found no malicious software.
      </>
    ),
    img: IMG_MAC,
    imgAlt: "macOS open confirmation dialog",
  },
  {
    label: (
      <>
        Click <StepKey>Open</StepKey> to continue
      </>
    ),
    desc: "The app will launch immediately. You won't see this prompt again on the same machine.",
    img: null,
  },
]

const winSteps = [
  {
    label: (
      <>
        Click <StepKey>More info</StepKey> on the blue screen
      </>
    ),
    desc: 'SmartScreen blocks unrecognized apps by default. Click "More info" to reveal the run option.',
    img: IMG_WIN1,
    imgAlt: "Windows SmartScreen — More info",
  },
  {
    label: (
      <>
        Click <StepKey>Run anyway</StepKey>
      </>
    ),
    desc: 'You\'ll see the app name (InterviewGuard.exe) confirmed. Click "Run anyway" and the installer will start.',
    img: IMG_WIN2,
    imgAlt: "Windows SmartScreen — Run anyway",
  },
]

const osConfig = {
  mac: {
    title: "Security notice — macOS",
    pill: "macOS",
    note: "Apple scanned this disk image for malicious software and none was detected. This prompt appears for all apps downloaded outside the Mac App Store.",
    steps: macSteps,
    dlHref: MAC_URL,
    dlBg: "#1c1c1e",
  },
  win: {
    title: "Security notice — Windows",
    pill: "Windows",
    note: "This warning appears because InterviewGuard hasn't yet built enough download history for SmartScreen to auto-trust it. The app is safe to run.",
    steps: winSteps,
    dlHref: WIN_URL,
    dlBg: "#0078d4",
  },
}

// ─── Shared UI primitives ────────────────────────────────────────────────────

const ModalShell = ({ children, maxWidth = 400 }) => (
  <div
    style={{
      background: "#fff",
      border: "0.5px solid rgba(0,0,0,0.14)",
      borderRadius: 14,
      width: "100%",
      maxWidth,
      overflow: "hidden",
      boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
      fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
    }}
  >
    {children}
  </div>
)

const ModalHeader = ({ left, right }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "14px 18px",
      borderBottom: "0.5px solid rgba(0,0,0,0.08)",
    }}
  >
    {left}
    {right}
  </div>
)

const ModalFooter = ({ left, right }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 18px",
      borderTop: "0.5px solid rgba(0,0,0,0.08)",
    }}
  >
    {left}
    {right}
  </div>
)

const CloseBtn = ({ onClick }) => (
  <button
    onClick={onClick}
    style={{
      width: 26,
      height: 26,
      border: "0.5px solid rgba(0,0,0,0.12)",
      borderRadius: 6,
      background: "transparent",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      color: "#888",
    }}
  >
    <X size={10} />
  </button>
)

// ─── Main download picker ────────────────────────────────────────────────────

const MainModal = ({ onSelect, onClose }) => {
  const cards = [
    {
      os: "mac",
      icon: (
        <img
          src={appleIcon}
          alt="Apple"
          width={20}
          height={20}
          style={{ objectFit: "contain", display: "block" }}
        />
      ),
      label: "Download for macOS",
      req: "macOS 12 Monterey or later",
    },
    {
      os: "win",
      icon: (
        <img
          src={windowsIcon}
          alt="Windows"
          width={20}
          height={20}
          style={{ objectFit: "contain", display: "block" }}
        />
      ),
      label: "Download for Windows",
      req: "Windows 10 / 11 (64-bit)",
    },
  ]

  return (
    <ModalShell maxWidth={400}>
      <ModalHeader
        left={
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <Shield size={13} color="#999" />
            <span
              style={{
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#999",
              }}
            >
              Get InterviewGuard
            </span>
          </div>
        }
        right={<CloseBtn onClick={onClose} />}
      />

      <div style={{ padding: "18px" }}>
        <p
          style={{
            fontSize: 13,
            color: "#666",
            lineHeight: 1.6,
            marginBottom: 16,
          }}
        >
          InterviewGuard is a lightweight desktop app that monitors your session
          for AI cheat tools. Install it before joining your interview.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {cards.map(({ os, icon, label, req, iconBg }) => (
            <button
              key={os}
              onClick={() => onSelect(os)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "13px 14px",
                border: "0.5px solid rgba(0,0,0,0.12)",
                borderRadius: 8,
                background: "#f9f9f9",
                cursor: "pointer",
                textAlign: "left",
                width: "100%",
                transition: "background 0.15s, border-color 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#fff"
                e.currentTarget.style.borderColor = "rgba(0,0,0,0.22)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f9f9f9"
                e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)"
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  background: iconBg,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {icon}
              </div>
              <div style={{ flex: 1 }}>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#111",
                    margin: 0,
                  }}
                >
                  {label}
                </p>
                <p
                  style={{
                    fontSize: 11,
                    color: "#aaa",
                    margin: "2px 0 0",
                    fontFamily: "monospace",
                  }}
                >
                  {req}
                </p>
              </div>
              <span style={{ color: "#bbb", fontSize: 16, lineHeight: 1 }}>
                ›
              </span>
            </button>
          ))}
        </div>
      </div>

      <ModalFooter
        left={
          <a
            href="https://resolut.co.in/interviewguard"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 11,
              color: "#aaa",
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            Learn more <ExternalLink size={9} />
          </a>
        }
        right={
          <button
            onClick={onClose}
            style={{
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#aaa",
              background: "transparent",
              border: "none",
              cursor: "pointer",
            }}
          >
            Close
          </button>
        }
      />
    </ModalShell>
  )
}

// ─── OS-specific warning modal ───────────────────────────────────────────────

const WarningModal = ({ os, onBack }) => {
  const cfg = osConfig[os]

  return (
    <div
      style={{
        background: "#fff",
        border: "0.5px solid rgba(0,0,0,0.14)",
        borderRadius: 14,
        width: "100%",
        maxWidth: 580,
        maxHeight: "80vh",
        overflow: "hidden",
        boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Fixed header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px",
          borderBottom: "0.5px solid rgba(0,0,0,0.08)",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Info size={13} color="#999" />
          <span style={{ fontSize: 13, fontWeight: 500, color: "#333" }}>
            {cfg.title}
          </span>
        </div>
        <span
          style={{
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            padding: "3px 8px",
            borderRadius: 4,
            background: os === "mac" ? "#e8e8ed" : "#cce4f7",
            color: os === "mac" ? "#1c1c1e" : "#004f8c",
          }}
        >
          {cfg.pill}
        </span>
      </div>

      {/* Scrollable body */}
      <div style={{ padding: "20px 22px", overflowY: "auto", flex: 1 }}>
        <p
          style={{
            fontSize: 13,
            color: "#666",
            lineHeight: 1.6,
            marginBottom: 20,
          }}
        >
          {os === "mac"
            ? "macOS will show a security prompt when you open InterviewGuard for the first time. This is normal — here's how to get past it."
            : "Windows SmartScreen will flag InterviewGuard because it's a new app. This is expected — follow these two steps to run it."}
        </p>

        {cfg.steps.map((step, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              gap: 14,
              marginBottom: i < cfg.steps.length - 1 ? 24 : 0,
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: "#f4f4f4",
                border: "0.5px solid rgba(0,0,0,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 500,
                color: "#666",
                flexShrink: 0,
                marginTop: 1,
              }}
            >
              {i + 1}
            </div>
            <div style={{ flex: 1 }}>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#111",
                  marginBottom: 6,
                }}
              >
                {step.label}
              </p>
              <p
                style={{
                  fontSize: 12.5,
                  color: "#666",
                  lineHeight: 1.6,
                  marginBottom: step.img ? 10 : 0,
                }}
              >
                {step.desc}
              </p>
              {step.img && (
                <div
                  style={{
                    borderRadius: 10,
                    overflow: "hidden",
                    border: "0.5px solid rgba(0,0,0,0.1)",
                    background: "#f4f4f4",
                  }}
                >
                  <img
                    src={step.img}
                    alt={step.imgAlt}
                    style={{ width: "100%", display: "block" }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}

        <div
          style={{
            marginTop: 20,
            padding: "11px 14px",
            background: "#f7f7f7",
            borderLeft: "2px solid rgba(0,0,0,0.12)",
            borderRadius: "0 8px 8px 0",
            fontSize: 12,
            color: "#666",
            lineHeight: 1.6,
          }}
        >
          {cfg.note}
        </div>
      </div>

      {/* Fixed footer */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "13px 20px",
          borderTop: "0.5px solid rgba(0,0,0,0.08)",
          flexShrink: 0,
        }}
      >
        <button
          onClick={onBack}
          style={{
            fontSize: 12,
            color: "#888",
            background: "transparent",
            border: "0.5px solid rgba(0,0,0,0.12)",
            borderRadius: 8,
            padding: "7px 13px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <ArrowLeft size={11} /> Back
        </button>
        <a
          href={cfg.dlHref}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: "#fff",
            background: cfg.dlBg,
            border: "none",
            borderRadius: 8,
            padding: "9px 18px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            textDecoration: "none",
          }}
        >
          <Download size={12} /> Download InterviewGuard
        </a>
      </div>
    </div>
  )
}

// ─── Root export ─────────────────────────────────────────────────────────────

const DownloadGuardModal = ({ isOpen, onClose }) => {
  const [selectedOS, setSelectedOS] = useState(null)

  if (!isOpen) return null

  const handleSelect = (os) => setSelectedOS(os)
  const handleBack = () => setSelectedOS(null)
  const handleClose = () => {
    setSelectedOS(null)
    onClose()
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
      }}
    >
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          backdropFilter: "blur(2px)",
        }}
      />

      {/* Modal — sits above backdrop */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: selectedOS ? 580 : 460,
          margin: "0 16px",
        }}
      >
        {selectedOS ? (
          <WarningModal os={selectedOS} onBack={handleBack} />
        ) : (
          <MainModal onSelect={handleSelect} onClose={handleClose} />
        )}
      </div>
    </div>
  )
}

export default DownloadGuardModal
